import { createPublicReader, type PublicReader } from '@pushcaster/api/public-read'
import type { components } from '@pushcaster/api/schema'

import { toStatusState, type StatusState } from './state'

/*
 * The three public endpoints, and the parsers that make a wire body safe to
 * render.
 *
 * The generated types are the contract, and they are not the whole story: they
 * describe what the API *promises*, and this page has to survive what it
 * actually receives from a deployment that may be newer, older, or in the middle
 * of failing. So every parser below is total — it never throws on a missing
 * optional, and it turns anything it does not recognise into `unknown` rather
 * than into a crash or, worse, a default.
 *
 * The one thing a parser *does* refuse is a body with no recognisable shape at
 * all: no `components` array, no `status` string. That becomes a `shape` failure
 * (§5.3) and the page says so, because a 200 containing something else is not
 * the same as the platform being healthy.
 */

/*
 * The generated schema is imported for the two enums the parsers narrow to —
 * `impact` and `state` are real unions in `schema.d.ts` (S3 declared them as
 * `ChoiceField`s for exactly this reason), so an exhaustive `switch` over an
 * incident state fails to compile when a state is added. `SystemStatus` itself
 * is deliberately *not* aliased here: this module's own interfaces are the
 * shape the page renders, and they differ from the wire on purpose —
 * `snake_case` becomes `camelCase`, and `state` narrows from `string` to the
 * six states of §5.1.
 */
type IncidentSummaryBody = components['schemas']['IncidentSummary']
type IncidentDetailBody = components['schemas']['IncidentDetail']

/** One component or region, as the page renders it. */
export interface StatusEntry {
  /** `key` for a component, `code` for a region. Stable; used for React keys
   *  and, for a region, to build the probe host at S5. */
  readonly id: string
  /** Server-rendered. The page carries no copy of the taxonomy (§4.1, D4). */
  readonly name: string
  readonly description: string
  readonly state: StatusState
  /** A short operator- or system-supplied phrase. `''` when there is nothing
   *  to add, which is the normal case for an operational component. */
  readonly reason: string
}

export interface IncidentSummary {
  readonly reference: string
  readonly title: string
  readonly impact: IncidentSummaryBody['impact']
  readonly state: IncidentSummaryBody['state']
  readonly startedAt: string
  /** `null` while the incident is open. The page renders "ongoing", not a blank. */
  readonly resolvedAt: string | null
  readonly components: readonly string[]
  readonly regions: readonly string[]
}

export interface IncidentUpdate {
  readonly createdAt: string
  readonly state: IncidentSummaryBody['state']
  readonly body: string
  /** A withdrawn entry is rendered struck through with its reason, never
   *  removed (§6.3). A timeline that quietly loses an entry is one nobody can
   *  rely on afterwards. */
  readonly withdrawn: boolean
  readonly withdrawnReason: string
}

export interface IncidentDetail extends IncidentSummary {
  readonly body: string
  readonly updates: readonly IncidentUpdate[]
}

export interface SystemStatus {
  readonly overall: StatusState
  readonly components: readonly StatusEntry[]
  readonly regions: readonly StatusEntry[]
  readonly activeIncidents: readonly IncidentSummary[]
  /** The API's own stamp, not the moment the response arrived. */
  readonly updatedAt: string
}

/* ── Parsing ─────────────────────────────────────────────────────────────── */

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null) throw new Error('not an object')
  return value as Record<string, unknown>
}

function asArray(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw new Error('not an array')
  return value
}

/** A string, or `''`. Never `undefined` reaching a template. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** A required, non-empty string. Its absence is a `shape` failure. */
function requiredText(value: unknown, field: string): string {
  if (typeof value !== 'string' || value === '') throw new Error(`missing ${field}`)
  return value
}

function parseEntry(value: unknown, idField: 'key' | 'code'): StatusEntry {
  const row = asRecord(value)
  return {
    id: requiredText(row[idField], idField),
    name: text(row.name) || requiredText(row[idField], idField),
    description: text(row.description),
    state: toStatusState(row.state),
    reason: text(row.reason),
  }
}

function parseSummary(value: unknown): IncidentSummary {
  const row = asRecord(value)
  return {
    reference: requiredText(row.reference, 'reference'),
    title: requiredText(row.title, 'title'),
    impact: text(row.impact) as IncidentSummaryBody['impact'],
    state: text(row.state) as IncidentSummaryBody['state'],
    startedAt: text(row.started_at),
    /* `null` and absent are the same fact here — still open — and both must
       stay distinct from a timestamp. `?? null` collapses only the two. */
    resolvedAt: typeof row.resolved_at === 'string' ? row.resolved_at : null,
    components: asArray(row.components ?? []).map((entry) => text(asRecord(entry).name)),
    regions: asArray(row.regions ?? []).map((entry) => text(asRecord(entry).name)),
  }
}

export function parseSystemStatus(body: unknown): SystemStatus {
  const row = asRecord(body)
  return {
    overall: toStatusState(row.status),
    components: asArray(row.components).map((entry) => parseEntry(entry, 'key')),
    regions: asArray(row.regions).map((entry) => parseEntry(entry, 'code')),
    activeIncidents: asArray(row.active_incidents ?? []).map(parseSummary),
    updatedAt: requiredText(row.updated_at, 'updated_at'),
  }
}

export function parseIncidentList(body: unknown): readonly IncidentSummary[] {
  return asArray(asRecord(body).incidents).map(parseSummary)
}

export function parseIncidentDetail(body: unknown): IncidentDetail {
  const row = asRecord(body)
  return {
    ...parseSummary(body),
    body: text(row.body),
    updates: asArray(row.updates ?? []).map((entry) => {
      const update = asRecord(entry)
      return {
        createdAt: text(update.created_at),
        state: text(update.state) as IncidentDetailBody['state'],
        body: text(update.body),
        withdrawn: update.withdrawn === true,
        withdrawnReason: text(update.withdrawn_reason),
      }
    }),
  }
}

/* ── The reader ──────────────────────────────────────────────────────────── */

/**
 * Built on first use, not at module load.
 *
 * `VITE_API_BASE_URL` is a build-time value. Constructing the reader at module
 * scope would throw during evaluation when it is missing, React would never
 * mount, and the reader would get a blank page instead of the unavailable state
 * this app exists to render. Deferring turns a build misconfiguration into
 * something the page can say out loud — which is the same reasoning
 * `apps/console/src/boot.tsx` gives for the same shape.
 */
let reader: PublicReader | null = null

function publicReader(): PublicReader {
  reader ??= createPublicReader({ origin: import.meta.env.VITE_API_BASE_URL })
  return reader
}

export function fetchSystemStatus(signal?: AbortSignal): Promise<SystemStatus> {
  return publicReader().read('/system/status', parseSystemStatus, signal ? { signal } : {})
}

export function fetchIncidentHistory(
  signal?: AbortSignal,
): Promise<readonly IncidentSummary[]> {
  return publicReader().read('/system/incidents', parseIncidentList, signal ? { signal } : {})
}

export function fetchIncident(
  reference: string,
  signal?: AbortSignal,
): Promise<IncidentDetail> {
  return publicReader().read(
    `/system/incidents/${encodeURIComponent(reference)}`,
    parseIncidentDetail,
    signal ? { signal } : {},
  )
}
